import * as React from 'react';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/utils';

interface Props {
  /** Page title — e.g. "Tenants". */
  title: string;
  /** Optional inline count rendered as `· 47` in monospace next to the title. */
  count?: number;
  /** Right-aligned actions (typically a primary button). */
  actions?: React.ReactNode;
  /** Optional secondary description shown beneath the title row. Use sparingly
   *  on list pages — Linear-style favours zero description. */
  description?: React.ReactNode;
  className?: string;
}

/**
 * Tight Linear-style page header for list pages. The title is mid-weight 20px
 * with a monospace count beside it; actions live on the right; no eyebrow,
 * no description by default. Vertical breathing room is intentionally tight
 * because a status-tabs row usually follows immediately.
 */
export function ListPageHeader({
  title,
  count,
  actions,
  description,
  className,
}: Props) {
  return (
    <header className={cn('flex flex-col gap-1 pb-1', className)}>
      <div className="flex items-end justify-between gap-3">
        <div className="flex items-baseline gap-2.5">
          <h1 className="text-[20px] font-semibold tracking-[-0.01em] text-foreground">
            {title}
          </h1>
          {count !== undefined && (
            <span className="font-mono text-[13px] text-muted-foreground/85 tabular-nums">
              · {fmt.number(count)}
            </span>
          )}
        </div>
        {actions && <div className="flex shrink-0 items-center gap-1.5">{actions}</div>}
      </div>
      {description && (
        <p className="max-w-2xl text-[12.5px] text-muted-foreground">{description}</p>
      )}
    </header>
  );
}
